// @ts-check
import { Flex, WhiteSection } from 'components/base/layout-components';
import GroupCycleTime from './GroupCycleTime/GroupCycleTime';
import GroupMetricsWidgets from './GroupMetricsWidget/GroupMetricsWidgets';
import {
  MIN_METRICS_TO_SELECT,
  MAX_METRICS_TO_SELECT,
  METRICS_MODAL_INSTRUCTIONS
} from './GroupMetricsWidget/GroupMetricsWidget.service';
import { useHistory } from 'react-router';
import {
  getMetricsNamesToFetch,
  getMetricsWidgetsWidths
} from './groupDashboard.service';
import useGroupTeams from './hooks/useGroupTeams/useGroupTeams';
import useGroupMetrics from './hooks/useGroupMetrics/useGroupMetrics';
import {
  GRANULARITY,
  GRANULARITY_TO_METRICS_V2_GRANULARITY
} from '../../performance/views/CustomReport/consts';
import { getGranularityByDates } from '../../performance/views/CustomReport/custom-report.util';
import useMetricsCalculationConfig from '../../../hooks/useMetricsCalculationConfig';
import { DefinitionLevels, SettingValues } from '@linearb/linjs-common';
import useSettingsService from 'components/hooks/useSettingsService';
import { useSelector } from 'react-redux';
import { uiStateSelector } from 'state/selectors/ui-state';
import { current5IterationsSelector } from 'state/selectors/iterations-picker';
import { CYCLE_TIME_METRICS } from 'common/constants/metrics';
import { uniq } from 'lodash';
import { useEffect, useMemo } from 'react';

export const GroupDashboardMetrics = ({
  toggleModal,
  widgetsMetricsNames,
  availableMetricsWidgets,
  setWidgetsMetricsNames,
  gitScopeToggle,
  setIsMetricsLoading
}) => {
  const uiState = useSelector(uiStateSelector);
  const { dates } = uiState;
  const current5Iterations = useSelector(current5IterationsSelector);
  const { metricsCalculationStrategy } = useMetricsCalculationConfig();
  const { selectedGroup } = useGroupTeams();
  const { cycleTimeWidth, widgetSectionWidth, metricsWidgetWidth } =
    getMetricsWidgetsWidths(widgetsMetricsNames.length);

  // we always need to bring cycle time metrics
  const metricNamesWithCycleTime = useMemo(
    () =>
      uniq([
        ...getMetricsNamesToFetch(widgetsMetricsNames),
        ...CYCLE_TIME_METRICS
      ]),
    [widgetsMetricsNames]
  );
  const granularity = getGranularityByDates({
    start_date: dates.from,
    end_date: dates.to,
    selectedGranularity: GRANULARITY.AUTO
  });
  const {
    isLoading: metricsPerGranularityLoading,
    metrics: metricsPerGranularity
  } = useGroupMetrics({
    metricsNames: metricNamesWithCycleTime,
    dates,
    metricsCalculationStrategy,
    selectedGroup,
    roll_up: GRANULARITY_TO_METRICS_V2_GRANULARITY[granularity],
    shouldGetOnlySelectedGroupData: true,
    gitScopeToggle
  });
  const { getSettingOrDefinitionValue } = useSettingsService();
  const { isLoading, metrics } = useGroupMetrics({
    metricsNames: metricNamesWithCycleTime,
    dates,
    metricsCalculationStrategy,
    selectedGroup,
    shouldGetOnlySelectedGroupData: true,
    gitScopeToggle
  });

  useEffect(() => {
    setIsMetricsLoading(isLoading);
  }, [isLoading]);

  const widgetsMetricsNamesInitialStateSettingValue =
    getSettingOrDefinitionValue({
      name: SettingValues.GROUP_DASHBOARD_METRICS_WIDGETS,
      definitionLevel: DefinitionLevels.TEAM,
      teamId: uiState?.selectedTeamId
    });

  const history = useHistory();

  return (
    <div className='flex'>
      <WhiteSection
        display='flex'
        borderRadius='0.45rem'
        height='22rem'
        width={`${cycleTimeWidth}%`}
        mb='0.75rem'
        data-cy='dashboard-group-view-cycle-time'
      >
        <GroupCycleTime
          toggleModal={toggleModal}
          uiState={uiState}
          history={history}
          current5Iterations={current5Iterations}
          selectedGroup={selectedGroup}
          metrics={metrics}
        />
      </WhiteSection>
      <Flex height='22rem' width={widgetSectionWidth}>
        <GroupMetricsWidgets
          metrics={metrics}
          selectedGroup={selectedGroup}
          widgetsMetricsNames={widgetsMetricsNames}
          metricsWidgetWidth={metricsWidgetWidth}
          toggleModal={toggleModal}
          availableMetricsWidgets={availableMetricsWidgets}
          setWidgetsMetricsNames={setWidgetsMetricsNames}
          metricsLoading={isLoading}
          metricsPerGranularityLoading={metricsPerGranularityLoading}
          isWidgetsSettingValue={widgetsMetricsNamesInitialStateSettingValue}
          metricsPerGranularity={metricsPerGranularity}
          minMetricsToSelect={MIN_METRICS_TO_SELECT}
          maxMetricsToSelect={MAX_METRICS_TO_SELECT}
          modalInstructions={METRICS_MODAL_INSTRUCTIONS}
        />
      </Flex>
    </div>
  );
};
